<template>
  <div v-if="article">
    <div class="max-w-4xl md:w-7/8 mx-auto px-8 md:px-20">

      <div v-if="getKeyVisualURL" class="thumbnail mt-16 md:hidden" :style="{'background-image' : `url(${getKeyVisualURL})`}">
        <a v-if="article.youtubeId" :href="`https://youtu.be/${article.youtubeId}`" class="video-thumb absolute top-0 left-0 w-full h-full" @click.exact.prevent="onModalYT(article.youtubeId)" />
      </div>

      <h1 class="text-2xl font-semibold mt-8">{{article.title}}</h1>

      <div class="my-6">
        <categories :categories="[...article.purposes]" />
      </div>

      <div class="flex items-center">
        <div class="text-sm leading-none">{{article.client}}</div>
        <div class="ml-auto text-xs">{{article.publishedAt}}</div>
      </div>

      <div class="mt-10 text-right lg:hidden">
        <div class="px-2 bg-white rounded shadow inline-flex ml-auto">
          <client-only placeholder="Loading...">
            <share-buttons :article="article" />
          </client-only>
        </div>
      </div>

      <hr class="border-gray-400 mt-10 md:hidden">
    </div>

    <div class="bg-secondary md:py-8 md:pb-16 mt-8 md:mt-24">
      <div class="max-w-4xl md:w-7/8 mx-auto">
        <contents-container>
          <template v-slot:sticky>
            <div class="py-2 w-12 bg-white rounded hidden lg:block shadow-lg">
              <client-only placeholder="Loading...">
                <share-buttons :article="article" />
              </client-only>
            </div>
          </template>

          <div v-if="getKeyVisualURL" class="thumbnail rounded-t -mt-24 hidden md:block" :style="{'background-image' : `url(${getKeyVisualURL})`}">
            <a v-if="article.youtubeId" :href="`https://youtu.be/${article.youtubeId}`" class="video-thumb absolute top-0 left-0 w-full h-full" @click.exact.prevent="onModalYT(article.youtubeId)" />
          </div>
          <div class="md:pt-10 pb-20 px-8 bg-white overflow-hidden md:rounded-b md:px-20">
            <div v-if="isContentsIndex" class="mt-8 md:mt-16 mb-12">
              <p>
                <span class="inline-block py-1 px-2 rounded-sm bg-gray-800 text-white text-sm leading-none">目次</span>
              </p>
              <ul class="mt-4 content-table">
                <li v-for="(content, index) in article.contents" :key="index">
                  <span v-if="content.addToIndex" :class="content.headingLevel">
                    <nuxt-link v-scroll-to="`#section-${index + 1}`" to>{{content.shortTitle}}</nuxt-link>
                  </span>
                </li>
              </ul>
            </div>

            <div class="sections">
              <section class="section" :id="`section-${index + 1}`" v-for="(content, index) in article.contents" :key="index">
                <template v-if="content.enableHeading">
                  <h2 v-if="content.headingLevel == 'h2'">{{content.title}}</h2>
                  <h3 v-else>{{content.title}}</h3>
                </template>
                <md v-if="content.enableBody" :md="content.body" />
              </section>
            </div>

            <hr class="border-gray-400 mt-20 mb-12">

            <div class="-mx-10 md:flex">
              <adjacent-article direction="prev" class="mr-auto" v-if="prevArticle" :article="prevArticle" />
              <adjacent-article direction="next" class="ml-auto" v-if="nextArticle" :article="nextArticle" />
            </div>
          </div>
        </contents-container>
      </div>
    </div>

    <ModalYouTube :show="showModalYt" :videoid="modalYtId" @close="onCloseModalYT" />
    <BreadcrumbsFoot :list="breadcrumbs" />
  </div>
</template>

<style lang="sass" scoped>
.thumbnail
  @apply bg-black bg-cover bg-center bg-no-repeat relative overflow-hidden
  padding-bottom: 50%
.contact-link, .signup-link
  @apply inline-block w-64 text-sm py-3 px-6 rounded-full text-center
.contact-link
  @apply border border-secondary bg-white text-secondary
.signup-link
  @apply bg-secondary text-white
@import ~/assets/css/article.sass
</style>

<script>
import mixin from '@/components/blog/article/mixin'
import AdjacentArticle from '@/components/contents/adjacentArticle.vue'
import ModalYouTube from "@/components/modalYouTube";
import ModalYouTubeMixin from "@/components/mixins/modalYouTube";
import BreadcrumbsFoot from '@/components/breadcrumbsFoot';
import Jsonld from "@/components/mixins/jsonld";

export default {
  mixins: [ mixin, ModalYouTubeMixin, Jsonld ],
  components: {
    AdjacentArticle,
    ModalYouTube,
    BreadcrumbsFoot
  },
  async middleware({ $strapi, params, redirect }) {
    if(Number.isInteger(Number(params.slug))) {
      const article = await $strapi.$http.$get(`tips?id=${params.slug}`);
      if(article.count > 0) {
        return redirect(`/contents/${article.data[0].slug}`)
      }
    };
  },
  async asyncData({ $strapi, params, error, store, app }) {
    let article, prevArticle, nextArticle
    const global = store.getters["cms/global"] || {}
    try {
      const tips = await $strapi.findOne("tips", params.slug)
      article = tips.data
      prevArticle = tips.prev
      nextArticle = tips.next

      if (!article) {
        throw new Error('Not found')
      }
    } catch (e) {
      error({
        statusCode: 404,
        message: e.message
      })
    }
    return {
      global,
      article,
      prevArticle,
      nextArticle,
      breadcrumbs: [
        { url: '/contents', name: 'お役立ちコンテンツ' },
        { name: article.title },
      ],
    };
  },
  computed: {
    isContentsIndex() {
      const { article } = this
      return article && article.contents && article.contents.some(e => e.addToIndex)
    }
  },
  head() {
    const { article } = this;
    const defaultSeo = this.global ? this.global.defaultSeo : {}
    const keywords = (article.keywords && article.keywords.substr(-1) === ',') ? article.keywords.slice(0, -1) : article.keywords
    const fullSeo = {
      ...defaultSeo,
      metaTitle: article.title,
      metaDescription: article.description,
      metaKeywords: `${keywords ? keywords.replace(/\s+/g, '') : this.$t('nav.contents')},${this.$t('keywords')}`
    };
    if (article.thumbnail) {
      fullSeo.shareImage = article.thumbnail
    }
    return {
      title: fullSeo.metaTitle,
      meta: this.$options.filters.cmsMetaTags(fullSeo)
    };
  },
  jsonld() {
    const ret = []
    const breadcrumbs = this.getBreadcrumbsJsonld(this.breadcrumbs)
    breadcrumbs && ret.push(breadcrumbs)
    return ret
  },
};
</script>